import { useState, useCallback, useMemo } from 'react';
import { authenticatedFetch } from '../utils/api';
import { API_HOST } from '../utils/constants';

/**
 * 自定义 Hook：管理角色、场景、道具资源选项
 * @returns {Object} 资源选项状态和方法
 */
export const useAssetOptions = () => {
  // === 状态管理 ===
  const [characterOptions, setCharacterOptions] = useState([]);
  const [sceneOptions, setSceneOptions] = useState([]);
  const [propOptions, setPropOptions] = useState([]);
  const [isRefreshingAssets, setIsRefreshingAssets] = useState(false);

  // === API 方法 ===

  /**
   * 异步获取资源数据
   * @param {string} projectId - 项目ID
   * @returns {Promise<Object>} 包含角色、场景、道具数据的 Promise
   */
  const fetchAssets = useCallback(async (projectId) => {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    const urls = [
      `${API_HOST}/api/characters?projectId=${projectId}`,
      `${API_HOST}/api/scenes?projectId=${projectId}`,
      `${API_HOST}/api/props?projectId=${projectId}`,
    ];

    const [characters, scenes, props] = await Promise.all(
      urls.map((url) => authenticatedFetch(url))
    );

    return {
      characters: characters || [],
      scenes: scenes || [],
      props: props || [],
    };
  }, []);

  /**
   * 刷新资源数据（带 loading 状态和错误处理）
   * @param {string} projectId - 项目ID
   * @returns {Promise<boolean>} 是否刷新成功
   */
  const refreshAssets = useCallback(async (projectId) => {
    setIsRefreshingAssets(true);
    try {
      const data = await fetchAssets(projectId);
      setCharacterOptions(data.characters);
      setSceneOptions(data.scenes);
      setPropOptions(data.props);
      return true;
    } catch (error) {
      console.error('Failed to refresh assets:', error);
      return false;
    } finally {
      setIsRefreshingAssets(false);
    }
  }, [fetchAssets]);

  // === 派生状态（使用 useMemo 优化） ===

  const allAssets = useMemo(
    () => ({
      characters: characterOptions,
      scenes: sceneOptions,
      props: propOptions,
    }),
    [characterOptions, sceneOptions, propOptions]
  );

  const assetCounts = useMemo(
    () => ({
      characters: characterOptions.length,
      scenes: sceneOptions.length,
      props: propOptions.length,
    }),
    [characterOptions, sceneOptions, propOptions]
  );

  // === 返回值 ===
  return {
    // 状态
    characterOptions,
    sceneOptions,
    propOptions,
    isRefreshingAssets,
    
    // 派生状态
    allAssets,
    assetCounts,
    
    // 方法
    fetchAssets,
    refreshAssets,

    // setter 方法（如有需要可直接操作）
    setCharacterOptions,
    setSceneOptions,
    setPropOptions,
  };
};

export default useAssetOptions;
